import axios_ from "../utils/axios";

// 给主页打标签接口在授权成功链路中可能会被重复触发。
// 这里按请求参数做一层全局单飞控制：同样的 page_id + label_ids 在请求未结束前只真正发出一次。
const pendingPageAddLabelRequests = new Map();

/**
 * page_add_label 的 page_id 支持单个主页，也支持逗号分隔的多个主页。
 * 这里统一兼容数组、数字和字符串，并去重后再提交，避免授权多主页时被拆成多次请求。
 * @param {string|number|Array<string|number>} pageId
 * @returns {string}
 */
function normalizePageIdParam(pageId) {
  const pageIds = Array.isArray(pageId) ? pageId : String(pageId || "").split(",");
  return [...new Set(pageIds.map((item) => String(item || "").trim()).filter(Boolean))].join(",");
}

/**
 * @param {("GET"|"get"|"POST"|"post")} method
 * @param {string} url
 * @param {Record<string,any>} params
 * @returns {{method: string, url: string, params: *, noCancelable: boolean, isFormData: boolean, isPyRequest: boolean, isFPyRequest: boolean}}
 */
function useConfig(method, url, params) {
  return {
    method,
    url,
    params,
    noCancelable: false,
    isFormData: false,
    isPyRequest: true,
    isFPyRequest: false,
  };
}

/**
 * 主页标签相关接口
 */
export default {
  page_label: {
    /**
     * 创建/编辑标签
     * 不传 label_id 时为新增标签；传 label_id 时为修改标签。
     * @param {{name:string; label_id?: number|string}} params
     * @returns {Promise<DefaultResponseType>}
     */
    create_label(params = {}) {
      return axios_(...Object.values(useConfig("POST", "api/pandagpt/v1/page_label/create_label", params)));
    },

    /**
     * 删除标签
     * @param {{label_id:number|string}} params
     * @returns {Promise<DefaultResponseType>}
     */
    delete_label(params = {}) {
      return axios_(...Object.values(useConfig("POST", "api/pandagpt/v1/page_label/delete_label", params)));
    },

    /**
     * 给主页添加标签
     * @param {{label_ids:string; page_id:string}} params
     * @returns {Promise<DefaultResponseType>}
     */
    page_add_label(params = {}) {
      // 统一整理请求参数，避免因为字段顺序或空白差异导致同一请求被视为不同请求。
      const normalizedParams = {
        page_id: normalizePageIdParam(params.page_id),
        label_ids: String(params.label_ids || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
          .sort()
          .join(","),
      };
      const requestKey = JSON.stringify(normalizedParams);

      // 同一组请求参数若已经在进行中，则直接复用之前的 Promise，不再重复发起网络请求。
      if (pendingPageAddLabelRequests.has(requestKey)) {
        return pendingPageAddLabelRequests.get(requestKey);
      }

      const requestPromise = axios_(
        ...Object.values(useConfig("POST", "api/pandagpt/v1/page_label/page_add_label", normalizedParams))
      ).finally(() => {
        pendingPageAddLabelRequests.delete(requestKey);
      });

      pendingPageAddLabelRequests.set(requestKey, requestPromise);
      return requestPromise;
    },

    /**
     * 给主页删除标签
     * @param {{page_label_id:number|string; page_id:string}} params
     * @returns {Promise<DefaultResponseType>}
     */
    page_del_label(params = {}) {
      return axios_(...Object.values(useConfig("POST", "api/pandagpt/v1/page_label/page_del_label", params)));
    },

    /**
     * 成员管理-所有标签
     * 管理员、超级管理员可以看到团队下所有标签，成员只可以看到自己拥有的标签。
     * @param {{user_id:string}} params
     * @returns {Promise<DefaultResponseType>}
     */
    page_labels(params = {}) {
      return axios_(...Object.values(useConfig("GET", "api/pandagpt/v1/page_label/page_labels", params)));
    },

    /**
     * 成员管理-基本信息
     * @param {{user_id:string}} params
     * @returns {Promise<DefaultResponseType>}
     */
    user_info(params = {}) {
      return axios_(...Object.values(useConfig("GET", "api/pandagpt/v1/page_label/user_info", params)));
    },

    /**
     * 成员管理-给用户增加/编辑标签及基础信息   编辑提交
     * @param {{user_id:string; team_id:string; label_ids:string; user_name?:string; role?:string|number; head_img_url?:string}} params
     * @returns {Promise<DefaultResponseType>}
     */
    edit_user_info(params = {}) {
      return axios_(...Object.values(useConfig("POST", "api/pandagpt/v1/page_label/edit_user_info", params)));
    },

    /**
     * 成员管理-用户标签
     * @param {{user_id:string}} params
     * @returns {Promise<DefaultResponseType>}
     */
    user_label(params = {}) {
      return axios_(...Object.values(useConfig("GET", "api/pandagpt/v1/page_label/user_label", params)));
    },

    /**
     * 成员管理-社交账号
     * @param {{user_id:string; platform?:string; page_name?:string; label_id?:string}} params
     * @returns {Promise<DefaultResponseType>}
     */
    user_accounts(params = {}) {
      return axios_(...Object.values(useConfig("GET", "api/pandagpt/v1/page_label/user_accounts", params)));
    },

    /**
     * 成员管理-按标签 ID 删除成员的单条授权标签。
     * page_id 由成员标签列表当前行的 id 传入，避免删除操作覆盖该成员的其余标签。
     * @param {{user_id:string; page_id:string|number}} params
     * @returns {Promise<DefaultResponseType>}
     */
    user_del_label(params = {}) {
      return axios_(...Object.values(useConfig("POST", "api/pandagpt/v1/page_label/user_del_label", params)));
    },
  },
};
